import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Container, Navbar, Button, Card } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/styles.css';

interface Song {
  id: number;
  title: string;
  artist: { name: string };
  songLyrics: string;
}

const LyricsDisplay = () => {
  const { songId } = useParams<{ songId: string }>();
  const [song, setSong] = useState<Song | null>(null);
  const [fontSize, setFontSize] = useState(16);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null); // Ref to the Card element
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSong = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songs/${songId}`);
        setSong(response.data);
      } catch (error) {
        console.error('Error fetching song lyrics:', error);
      }
    };
    fetchSong();

    // Add event listener for fullscreen change
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    // Cleanup
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [songId]);

  const increaseFontSize = () => setFontSize(prev => Math.min(prev + 5, 50));
  const decreaseFontSize = () => setFontSize(prev => Math.max(prev - 5, 12));

  const toggleFullScreen = async () => {
    if (!cardRef.current) return;

    try {
      if (!isFullScreen) {
        // Enter fullscreen
        await cardRef.current.requestFullscreen();
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  const handleBack = () => navigate('/bandleader');

  const adjustFontSizeInHtml = (html: string, newFontSize: number) => {
    // Check if the HTML already contains font-size styles
    const fontSizeRegex = /font-size:\s*\d+px/g;
    if (fontSizeRegex.test(html)) {
      // Replace existing font-size values
      return html.replace(fontSizeRegex, `font-size: ${newFontSize}px`);
    } else {
      // Wrap the HTML in a div with the desired font-size
      return `<div style="font-size: ${newFontSize}px">${html}</div>`;
    }
  };

  if (!song) {
    return (
      <Container className="mt-5 text-center">
        <h3 className="text-light">Loading lyrics...</h3>
      </Container>
    );
  }

  const adjustedLyrics = adjustFontSizeInHtml(song.songLyrics || 'No lyrics available.', fontSize);

  return (
    <>
      <Navbar expand="lg" className="navbar-custom shadow-sm">
        <Container>
          <Navbar.Brand className="d-flex align-items-center animate__animated animate__fadeIn">
            <img
              src="/logo.png"
              alt="Karaoke Logo"
              style={{ maxWidth: '50px', marginRight: '15px', transition: 'transform 0.3s ease' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
            <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.5rem', color: 'white', fontWeight: 500 }}>
              Karaoke Lyrics
            </span>
          </Navbar.Brand>
          <Button
            variant="outline-light"
            onClick={handleBack}
            style={{ fontFamily: "'Montserrat', sans-serif", borderColor: '#ff6b6b', color: '#ff6b6b', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#ff6b6b';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#ff6b6b';
            }}
          >
            Back to Dashboard
          </Button>
        </Container>
      </Navbar>

      <Container fluid className="mt-5 mb-5">
        <Card 
          ref={cardRef} // Add ref to the Card
          className="card-custom mx-auto" 
          style={{ 
            maxWidth: '900px',
            background: 'rgba(255, 255, 255, 0.9)'
          }}
        >
          <Card.Header
            as="h2"
            className="text-center"
            style={{
              background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)',
              color: 'white',
              fontFamily: "'Poppins', sans-serif",
              padding: '20px',
            }}
          >
            {song.artist.name} - {song.title}
          </Card.Header>
          <Card.Body className="p-4">
            <div className="d-flex justify-content-center mb-4">
              <Button
                className="btn-custom-primary me-2"
                onClick={decreaseFontSize}
                disabled={fontSize <= 12}
              >
                - Zoom Out
              </Button>
              <Button
                className="btn-custom-primary me-2"
                onClick={increaseFontSize}
                disabled={fontSize >= 50}
              >
                + Zoom In
              </Button>
              <Button
                className="btn-custom-primary"
                onClick={toggleFullScreen}
              >
                {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
              </Button>
            </div>
            <div
              className="lyrics-content"
              style={{
                lineHeight: '1.5',
                color: '#333',
                padding: '20px',
                background: '#f9f9f9',
                borderRadius: '10px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                maxHeight: '70vh',
                overflowY: 'auto',
                ...(isFullScreen && {
                  height: 'calc(100vh - 150px)', // Adjust height for fullscreen
                })
              }}
              dangerouslySetInnerHTML={{ __html: adjustedLyrics }}
            />
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default LyricsDisplay;